import { faCircleQuestion as fasCircleQuestion } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { AbstractResultList } from "@component/assets/AbstractListingCalculator/AbstractResultList";
import type { AbstractResultListProps } from "@component/assets/AbstractListingCalculator/AbstractResultList";
import { getBankName } from "@component/assets/data/banks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AbTest } from "@ui/AbTest";
import { ExpertBlockquote } from "@ui/components/ExpertBlockquote";
import { Button, InfoPopup, LandingContainer, List } from "bm-ui-kit";
import type { ReactNode } from "react";
import React from "react";
import { Col, Collapse, Container, Row } from "react-bootstrap";
import { PersonalLoanLeadForm } from "../LeadForm/PersonalLoanLeadForm";
import { PersonalLoanResultItem } from "./PersonalLoanResultItem";
import { SORT_OPTIONS } from "./data";
import { getInterestName, getLeadCalculatorRequest } from "./domain";
import type {
	PersonalLoanRequest,
	PersonalLoanResponseItem,
	PersonalLoanSearchParams,
	PersonalLoanSort,
} from "./types";

interface Props
	extends AbstractResultListProps<
		PersonalLoanSearchParams,
		PersonalLoanSort,
		PersonalLoanRequest,
		PersonalLoanResponseItem
	> {
	mobileView?: boolean;
	hideLeadForm?: boolean;
}

export class PersonalLoanResultList extends AbstractResultList<
	PersonalLoanSearchParams,
	PersonalLoanSort,
	PersonalLoanRequest,
	PersonalLoanResponseItem,
	Props
> {
	protected renderResult(props): ReactNode {
		const { mobileView, hideLeadForm } = this.props;

		return <PersonalLoanResultItem mobileView={mobileView} hideLeadForm={hideLeadForm} {...props} />;
	}

	protected renderHeader(title: ReactNode): ReactNode {
		return (
			<div className="container">
				<div className="h2 my-4 text-center">{title}</div>
			</div>
		);
	}

	protected renderNormalListTitle(): ReactNode {
		return this.renderTooltip("Banki ajánlatok");
	}

	protected renderBankSpecifictTitle(): ReactNode {
		const { highlightedBank } = this.props;

		return this.renderTooltip(`${getBankName(highlightedBank)} ajánlatok`);
	}

	protected renderOtherBankTitle(): ReactNode {
		return this.renderTooltip("A legjobb találatok");
	}

	private renderTooltip(children: ReactNode): ReactNode {
		return (
			<InfoPopup
				className="d-none d-md-inline-block"
				tooltip="A kalkulátor a banki ajánlatokat a részletes keresőben kiválasztott THM, teljes visszafizetés vagy törlesztőrészlet szerint rendezi sorba."
			>
				{children} <strong className="font-weight-black">{this.getSortName()}</strong> szerint rendezve
				<FontAwesomeIcon icon={fasCircleQuestion} className="ml-1" />
			</InfoPopup>
		);
	}

	protected renderListingOffer(): ReactNode {
		const { searchParams } = this.props;
		const { showListingOffer } = this.state;

		return (
			<>
				<LandingContainer className="text-white text-center my-3" bgClassName="bg-retail-gradient">
					<div className="h3 mb-3">
						<span>
							<AbTest version="A">
								Egyeztess szakértőnkkel díjmentesen: <br />
								kapd meg a legkedvezőbb fix kamatozású kölcsönt akár{" "}
								<span className="text-gold"> 72 órán belül</span>.
							</AbTest>
							<AbTest version="B">
								Tudjuk, hol kapod meg a számodra legjobb fix kamatozású hitelt a leggyorsabban.
								<br />
								Egyeztessen Bankmonitor szakértőnkkel <span className="text-gold">díjmentesen</span>.
							</AbTest>
						</span>
					</div>
					<Button
						size="lg"
						className="mt-1 px-5 bg-white font-weight-bold text-primary border-none"
						onClick={this.onListingOfferClick}
					>
						Érdekel
					</Button>
				</LandingContainer>
				<Collapse in={showListingOffer}>
					<div className=" mt-3">
						<Container>
							<Row className="justify-content-center align-items-center flex-column-reverse flex-lg-row p-3">
								<Col className="text-block" lg={7}>
									<div className="h3 font-weight-bold mb-4">
										<AbTest version="A">Szakértőnk díjmentesen segít:</AbTest>
										<AbTest version="B">Mi a fontos számodra kölcsön igénylés esetén?</AbTest>
									</div>
									<List itemStyle="tick">
										<AbTest version="A">
											<List.Item>
												<strong>Átlátni és kiválasztani</strong> a piacon lévő termékek közül
												azt, ami a pénzügyi helyzeted alapján személyre szabottan a számodra
												legkedvezőbb ajánlat.
											</List.Item>
											<List.Item>
												<strong>Elmagyarázni</strong> neked a bankok apróbetűs kitételeit, hogy
												minden információ tudatában hozhasd meg a legjobb döntést.
											</List.Item>
											<List.Item>
												<strong>Végig kíséri</strong> a hitelügyintézés teljes folyamatát veled,
												egészen addig, amíg megkapod a folyósítást.
											</List.Item>
											<List.Item>
												Ráadásul <strong>utánkövet</strong>i helyetted a hitelpiac alakulását és
												szól, ha elérhető kedvezőbb ajánlat a meglévő kölcsönödnél.
											</List.Item>
										</AbTest>
										<AbTest version="B">
											<List.Item>
												<strong>A gyorsaság?</strong> Megmondjuk, hol kapod meg a leggyorsabban.
											</List.Item>
											<List.Item>
												<strong>A biztonság?</strong> A kamat végig fix, a többi kockázatot
												szakértőnk elmagyarázza.
											</List.Item>
											<List.Item>
												<strong>Az alacsony költség?</strong> Megtaláljuk a legjobb kamatot,
												vagy a legalacsonyabb törlesztőt.
											</List.Item>
											<List.Item>
												<strong>Az egyszerűség?</strong> Szakértőnk végig kíséri veled a
												hitelügyintézés teljes folyamatát.
											</List.Item>
										</AbTest>
									</List>
									<div>
										<AbTest version="A">
											<h4>Hogyan lehet szolgáltatásunk ingyenes?</h4>
											<p>
												A törvényi előírások alapján kizárólag a bank fizethet kompenzációt a
												bankfiókok helyett elvégzett munka ellenértékeként.
											</p>
										</AbTest>
										<AbTest version="B">
											<h4>Mindez mennyiért?</h4>
											<p>
												<strong>Szolgáltatásunk díjmentes:</strong> a törvényi előírások alapján
												kizárólag a bank fizethet kompenzációt a bankfiókok helyett elvégzett
												munka ellenértékeként.
											</p>
										</AbTest>
									</div>
									<ExpertBlockquote
										expertId="dekany"
										quote="A Bankmonitornál célunk, hogy ügyfeleink pénzügyeit egészségesen tartsuk. Épp ezért, nem csak egy egyszeri hitelügyintézésnél vagyunk mellettük, hanem minden élethelyzetben számíthatnank ránk."
										title="Személyi kölcsön csoportvezető"
									/>
								</Col>
								<Col className="pb-4 pb-lg-0" lg={5} md={8}>
									{showListingOffer && (
										<PersonalLoanLeadForm
											{...this.getListingOfferLeadProps()}
											title="Kérj segítséget szakértőnktől!"
											request={getLeadCalculatorRequest(searchParams)}
										/>
									)}
								</Col>
							</Row>
						</Container>
					</div>
				</Collapse>
			</>
		);
	}

	private getSortName(): string {
		const {
			searchParams: { sort },
		} = this.props;

		return SORT_OPTIONS.find((item) => item.value === sort)?.label;
	}

	protected createViewItemListEventItem(result: PersonalLoanResponseItem): Record<string, any> {
		const {
			searchParams: { loanAmount },
		} = this.props;

		return {
			coupon: "none",
			currency: "HUF",
			discount: 0,
			item_category: result.gracePeriodInstallment ? result.gracePeriodInstallment : "",
			item_category2: result.totalRedemption,
			item_category3: getInterestName(result.interestPeriod),
			item_category4: result.startupExpenses,
			item_category5: loanAmount,
			item_variant: "",
			price: result.apr,
			quantity: Math.round(result.interestRate * 10_000),
		};
	}

	protected insertOffer(list: ReactNode): ReactNode {
		const { hideLeadForm } = this.props;

		if (Array.isArray(list) && list.length > 2) {
			const newList: ReactNode[] = list.length > 3 ? [...list, this.renderAds2()] : list;

			if (!hideLeadForm) {
				newList.splice(3, 0, this.renderListingOffer());
			}

			if (list.length > 7) {
				newList.splice(5, 0, this.renderAds1());
			}

			return newList;
		}

		return list;
	}

	protected getComponentName(): string {
		return "PersonalLoanResultList";
	}
}
